/**
 * Loads whichever table the current view needs.
 *
 * Two things this handles that the original did not: every request is aborted
 * when its inputs change, so a slow response for an old filter can no longer
 * overwrite a newer one; and previous rows stay on screen while the next
 * request is in flight, so the table doesn't blank out between keystrokes.
 */

import { useEffect, useState } from "react";

import { ApiError, api, isAbort } from "../api/client";
import type { Building, Page, Unit } from "../api/types";
import {
  toBuildingQuery,
  toUnitQuery,
  type Filters,
  type View,
} from "../lib/filters";

type Rows =
  | { view: "buildings"; rows: Building[] }
  | { view: "units"; rows: Unit[] };

interface State {
  data: Rows;
  page: Page | null;
  loading: boolean;
  error: string;
  /** Set when the failure was a 402, so the caller can show the upsell. */
  upgradeNeeded: boolean;
}

const EMPTY_PAGE: Page = {
  limit: 0,
  offset: 0,
  total: 0,
  capped: false,
  true_total: null,
};

export function useRows(view: View, filters: Filters): State {
  const [state, setState] = useState<State>({
    data: { view: "buildings", rows: [] },
    page: null,
    loading: true,
    error: "",
    upgradeNeeded: false,
  });

  useEffect(() => {
    const controller = new AbortController();
    setState((previous) => ({ ...previous, loading: true, error: "" }));

    const request =
      view === "buildings"
        ? api
            .buildings(toBuildingQuery(filters), controller.signal)
            .then((response) => ({
              data: { view: "buildings" as const, rows: response.rows },
              page: response.page,
            }))
        : api
            .units(toUnitQuery(filters), controller.signal)
            .then((response) => ({
              data: { view: "units" as const, rows: response.rows },
              page: response.page,
            }));

    request
      .then(({ data, page }) =>
        setState({ data, page, loading: false, error: "", upgradeNeeded: false }),
      )
      .catch((cause: unknown) => {
        if (isAbort(cause)) return;
        const apiError = cause instanceof ApiError ? cause : null;
        setState({
          data: { view, rows: [] } as Rows,
          page: EMPTY_PAGE,
          loading: false,
          error: apiError?.message ?? "Something went wrong.",
          upgradeNeeded: apiError?.status === 402,
        });
      });

    return () => controller.abort();
  }, [view, filters]);

  return state;
}
